// Copyright Epic Games, Inc. All Rights Reserved.

import { clientFetch } from "./clientFetch";
import { default as JSONbig } from "@epic/json-bigint";
import { getLocale } from "../store/localization/lib/getLocale";

export const accessTokenHeaderName = "x-main-service-access-token";

// Use custom JSON serializer because built-in stuff does not support BigInt
// parsing out of the box.
const serializer = JSONbig({ alwaysParseAsBig: true });

export enum EInstallationStatus {
  Queued = "Queued",
  Initializing = "Initializing",
  Resuming = "Resuming",
  Installing = "Installing",
  BuildVerification = "BuildVerification",
  CleanUp = "CleanUp",
  PrerequisitesInstall = "PrerequisitesInstall",
  Completed = "Completed",
  Paused = "Paused",
}

interface IProgress {
  taskId: string;
  title: string;
  status: EInstallationStatus;
  completionProgress: number;
  averageDownloadSpeed: bigint;
  averageDiskReadSpeed: bigint;
  averageDiskWriteSpeed: bigint;
  downloadedBytes: bigint;
  diskReadBytes: bigint;
  diskWriteBytes: bigint;
  totalDownloadRequiredBytes: bigint;
}

interface ITaskProgress extends IProgress {
  completed: false;
  alertCode: string;
  alertDescription: string;
}

interface ICompletedTaskProgress extends IProgress {
  completed: true;
  errorCode: string;
  errorTitle: string;
  errorDescription: string;
}

export type IInstallationProgress = ITaskProgress | ICompletedTaskProgress;

export interface IInstallationGroupProgressMessage {
  currentTaskProgress: IInstallationProgress;
  currentTaskId: string;
  currentTaskNumber: number;
  numInstalled: number;
  numInstallations: number;
}

export async function getInstallationProgress(
  installationGroupId: string,
  mainServiceAccessToken: string
): Promise<IInstallationGroupProgressMessage> {
  const response = await clientFetch(
    `/api/installation/group/${installationGroupId}/progress?locale=${getLocale()}`,
    {
      headers: {
        [accessTokenHeaderName]: mainServiceAccessToken,
      },
    }
  );

  const body = await response.text();

  const parsedBody = serializer.parse(
    body
  ) as IInstallationGroupProgressMessage;

  // All these fields sent as numbers, but the parser parses them as BigInt.
  parsedBody.numInstallations = Number(parsedBody.numInstallations);
  parsedBody.numInstalled = Number(parsedBody.numInstalled);
  parsedBody.currentTaskNumber = Number(parsedBody.currentTaskNumber);
  parsedBody.currentTaskProgress.completionProgress = Number(
    parsedBody.currentTaskProgress.completionProgress
  );

  return parsedBody;
}

export async function cancelInstallation(
  installationGroupId: string,
  mainServiceAccessToken: string
): Promise<void> {
  await clientFetch(`/api/installation/group/${installationGroupId}/cancel`, {
    method: "POST",
    headers: {
      [accessTokenHeaderName]: mainServiceAccessToken,
    },
  });
}
